import { useMemo, useState } from "react";
import { ItemEntry } from "./item-entry";
import type { ExplorerItem } from "../types";

const toIdSet = (value: unknown): Set<number> => {
  if (value instanceof Set) return value;
  if (value != null && typeof (value as any)[Symbol.iterator] === "function") {
    return new Set(value as Iterable<number>);
  }
  return new Set();
};

type Props = {
  item: ExplorerItem;
  level: number;
  onClick?: (item: ExplorerItem, e: React.MouseEvent) => void;
  onDoubleClick?: (item: ExplorerItem) => void;
  selectedItems?: Set<number>;
  disabledItemIds?: Set<number>;
  windowId?: string;
  showGroupId?: boolean;
  activeItemId?: number | null;
};

export const ItemTreeNode = ({
  item,
  level,
  onClick,
  onDoubleClick,
  selectedItems,
  disabledItemIds,
  windowId,
  showGroupId,
  activeItemId,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const selected = useMemo(() => toIdSet(selectedItems), [selectedItems]);
  const disabled = useMemo(() => toIdSet(disabledItemIds), [disabledItemIds]);

  const hasChildren = "children" in item && !!(item.children && item.children.length > 0);
  const children: ExplorerItem[] = hasChildren ? item.children : [];
  const isDisabled = !hasChildren && disabled.has(item.id);

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();

    if (isDisabled) return;

    if (hasChildren) {
      setIsExpanded(!isExpanded);
      return;
    }

    if (onClick) {
      onClick(item, e as React.MouseEvent);
    }
  };

  const handleDoubleClick = () => {
    if (!hasChildren && onDoubleClick) {
      onDoubleClick(item);
    }
  };

  return (
    <div className="select-none">
      <ItemEntry
        item={item}
        level={level}
        isSelected={selected.has(item.id)}
        hasChildren={hasChildren}
        isExpanded={isExpanded}
        setIsExpanded={() => setIsExpanded((prev) => !prev)}
        handleClick={handleClick}
        handleDoubleClick={onDoubleClick ? handleDoubleClick : undefined}
        isDisabled={isDisabled}
        windowId={windowId}
        activeItemId={activeItemId}
      />

      {hasChildren && isExpanded && (
        <div className="relative">
          <div
            className="absolute top-1 bottom-1.25 w-px bg-gray-300"
            style={{ left: `${level * 16 + 12}px` }}
          />
          <div className="space-y-0.5 pt-0.75">
            {children.map((child) => (
              <ItemTreeNode
                key={child.id}
                item={child}
                level={level + 1}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                selectedItems={selected}
                disabledItemIds={disabled}
                windowId={windowId}
                showGroupId={showGroupId}
                activeItemId={activeItemId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
